import React, { useContext, useState } from 'react';
// Util
import { getBezierPath, getMarkerEnd } from 'reactflow';
import { createSaveEvent } from '../../../../../utils/createSaveEvent';
import { saveCanvasEvent } from '../../../../../utils/canvasApi';
import { CanvasContext } from '../../WorkflowCanvas';
import useProjectStore from '../../../../ProjectCanvas/useProjectStore';
import { getElements } from '../../../../../utils/CanvasUtil';

// MUI
import { withStyles } from '@mui/styles';
import LoopIcon from '@mui/icons-material/Loop';
import { Tooltip } from '@mui/material';

// Zustand
import useCanvasStore from '../../useCanvasStore';
import usePlaceholderTraceStore from '../../../../Placeholders/usePlaceholderTraceStore';
import LinkLabel from './EdgeUtils/LinkLabel';
import { EdgeLabelRenderer } from 'reactflow';
import { getTriggerLabels } from '../creationPortLabels';
import { TRIGGER_LABEL_Y_OFFSET } from '../../../../../utils/layoutConstants';

const styles = {
  icon: {
    color: '#E10000',
    cursor: 'pointer',
    height: '20px',
    width: '20px',
    ' &:hover': {
      color: '#E10000',
    },
  },
  editIcon: {
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    height: '20px',
    width: '20px',
    ' &:hover': {
      color: 'rgba(0, 0, 0, 0.65) !important',
    },
  },
};

const CAN_SHOW_TTC_ON_LINK = ['PROGRESSVIEW'];

const TriggerEdgeLabel = ({ x, y, text }) => (
  <EdgeLabelRenderer>
    <div
      className="nodrag nopan"
      style={{
        position: 'absolute',
        transform: `translate(-50%, -50%) translate(${x}px, ${y - TRIGGER_LABEL_Y_OFFSET}px)`,
        background: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '12px',
        padding: '2px 8px',
        fontSize: '11px',
        lineHeight: 1.4,
        color: 'rgba(0, 0, 0, 0.72)',
        whiteSpace: 'nowrap',
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
        pointerEvents: 'none',
      }}
    >
      {text}
    </div>
  </EdgeLabelRenderer>
);

const Link = ({
      id,
      source,
      sourceX,
      sourceY,
      target,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
      borderRadius,
      centerX,
      centerY,
      data,
      label,
      arrowHeadType,
      markerEndId,
      selected,
      classes,
      style,
      hasDb,
      markerEnd,
    }) => {
      const nodes = useCanvasStore(s => s.nodes);
      const edges = useCanvasStore(s => s.edges);
      const isTraced = usePlaceholderTraceStore(s => s.traceEdgeIds.has(id));

      const { modeInfo } = useContext(CanvasContext);
      const { savingInfo } = useContext(CanvasContext);
      const { canvasInstance } = useContext(CanvasContext);
      const { updated } = useContext(CanvasContext);

      const [lastModified, setLastModified] = updated ? updated : useState({});

      const [reactFlowInstance, setReactFlowInstance] = canvasInstance
        ? canvasInstance
        : useState({});

      // we don't multiply with zoom because the coordinates don't change with zoom.
      const [saving, setSaving] = savingInfo ? savingInfo : useState({});

      if (!nodes) {
        const edgePath = getBezierPath({
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
          borderRadius,
          centerX,
          centerY,
        });
        markerEnd = getMarkerEnd(arrowHeadType, markerEndId);
        return (
          <>
            <path
              id={id}
              style={style}
              className={`react-flow__edge-path${isTraced ? ' placeholder-traced-path' : ''}`}
              d={edgePath}
              markerEnd={markerEnd}
            />
            ;
          </>
        );
      } else {
        const [mode, setMode] = modeInfo;

        let edgePath = getBezierPath({
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
          centerX,
          centerY,
        });

        const edgeCenterX = (sourceX + targetX) / 2;
        const edgeCenterY = (sourceY + targetY) / 2;
        const foreignObjectSize = 30;

        const handleDelete = async () => {
          if (saving) return;

          setSaving(true);

          if (mode === 'DEFAULT') {
            // Remove the link
            let result = edges.filter(obj => {
              return obj.id != id;
            });
            useCanvasStore.getState().setEdges(result);
          }

          let rfiObj = reactFlowInstance.toObject();

          let viewport = {
            x: rfiObj.viewport.x.toFixed(5),
            y: rfiObj.viewport.y.toFixed(5),
            zoom: rfiObj.viewport.zoom.toFixed(5),
          };

          //this is a component link
          let eventData = {
            sourceComponentId: source,
            targetComponentId: target,
            noteId: id,
          };

          let saveEvent = createSaveEvent(
            'DELETE_LINK',
            useProjectStore.getState().project_id,
            viewport,
            getElements(reactFlowInstance),
            eventData
          );

          //send event
          await saveCanvasEvent(useProjectStore.getState().project_id, saveEvent)
            .then(response => {
              if (response.data && response.data.updated) {
                setLastModified(response.data.lastModified);
              }
              //insert delay here
              setSaving(false);
            })
            .catch(error => {
              console.log(error);
            });
        };

        const getCorrectEventDataFormat = updatedItem => {
          try {
            return {
              arrowHeadType: 'arrowclosed',
              id: updatedItem.id,
              source: updatedItem.source,
              target: updatedItem.target,
              sourceHandle: updatedItem.sourceHandle,
              targetHandle: updatedItem.targetHandle,
              type: 'link',
              style: updatedItem.style,
              data: {
                label: updatedItem.data ? updatedItem.data.label : '',
              },
            };
          } catch (e) {
            return;
          }
        };

        const handleSaveLinkLabel = async newLabel => {
          if (saving) return;

          setSaving(true);

          let rfiObj = reactFlowInstance.toObject();

          let viewport = {
            x: rfiObj.viewport.x.toFixed(5),
            y: rfiObj.viewport.y.toFixed(5),
            zoom: rfiObj.viewport.zoom.toFixed(5),
          };

          // Update the label in the canvas store
          let updatedItem;
          const newEdges = edges.map(n => {
            if (n.id !== id) return n;
            updatedItem = {
              ...n,
              data: {
                label: newLabel,
              },
            };
            return updatedItem;
          });

          useCanvasStore.getState().setEdges(newEdges);

          // Construct event data in correct format
          let eventData = getCorrectEventDataFormat(updatedItem);
          if (!eventData) {
            setSaving(false);
            return;
          }

          // Save label in backend
          let saveEvent = createSaveEvent(
            'UPDATE_NOTE',
            useProjectStore.getState().project_id,
            viewport,
            getElements(reactFlowInstance),
            eventData
          );

          //send event
          await saveCanvasEvent(useProjectStore.getState().project_id, saveEvent)
            .then(response => {
              if (response.data && response.data.updated) {
                setLastModified(response.data.lastModified);
              }
              setSaving(false);
            })
            .catch(error => {
              console.log(error);
            });
        };

        const isSelected =
          selected == true && !(data && data.hasDb) && !(data && data.showDeleteButton);

        const markedAsRemove = data && data.editStatus === 'TO_DELETE' ? true : false;
        
        // Check if this is a loop edge
        const isLoopEdge = data && data.isLoop;
        const maxIterations = data && data.maxIterations;

        // Self-documenting trigger label for wait-state sources. Two cases
        // pin a label onto the edge:
        //   • CREATION edges (the secondary "On creation" port) → "creation" copy
        //   • Completion edges from a wait-state source        → "submission" copy
        // Wording comes from `creationPortLabels` so it stays in lockstep
        // with the on-node hints in InlineNodeView. Loop / DB / gateway
        // edges already carry their own visual treatment and are excluded
        // so we don't double-label them.
        const isCreationEdge = data && data.triggerOn === 'CREATION';
        const sourceNode = nodes && nodes.find(n => n.id === source);
        const sourceTriggerLabels = sourceNode ? getTriggerLabels(sourceNode.data?.type) : null;
        const triggerLabelText = sourceTriggerLabels
          ? isCreationEdge
            ? sourceTriggerLabels.creation
            : !isLoopEdge && !(data && data.hasDb)
              ? sourceTriggerLabels.submission
              : null
          : null;

        // Determine the marker to use based on edge type
        const getMarkerEnd = () => {
          if (data && data.hasDb) return 'url(#myMarker)';
          return markerEnd;
        };

        return (
          <>
            <marker
              id="myMarker"
              viewBox="0 0 10 10"
              refX="5"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto"
            >
              <path d="M 0 0 L 7 5 L 0 10 z" fill="#1C1B1F" />
            </marker>
            <path
              id={id}
              style={{
                ...style,
                stroke: markedAsRemove ? 'rgba(0, 0, 0, 0.2)' : style.stroke,
                strokeDasharray: isLoopEdge ? '5,5' : (markedAsRemove ? '12, 8' : style.strokeDasharray),
              }}
              className={`react-flow__edge-path${isTraced ? ' placeholder-traced-path' : ''}`}
              d={edgePath ? edgePath[0] : edgePath}
              markerEnd={getMarkerEnd()}
            />

            <LinkLabel
              classes={classes}
              label={data ? data.label : ''}
              handleDelete={handleDelete}
              edgePath={edgePath}
              isComponentLink={label && (label === 'componentLink' || label === 'loopLink')}
              isSelected={isSelected}
              saving={saving}
              handleSaveLinkLabel={handleSaveLinkLabel}
              markedAsRemove={markedAsRemove}
            />

            {triggerLabelText && edgePath && (
              <TriggerEdgeLabel
                x={edgePath[1]}
                y={edgePath[2]}
                text={triggerLabelText}
              />
            )}

            {/* Loop edge indicator */}
            {isLoopEdge && (
              <EdgeLabelRenderer>
                <Tooltip title={`Loop: runs up to ${maxIterations || 10} times`} placement="top">
                  <div
                    style={{
                      position: 'absolute',
                      transform: `translate(-50%, -50%) translate(${edgeCenterX}px, ${edgeCenterY}px)`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundColor: '#9c27b0',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 500,
                      pointerEvents: 'all',
                      cursor: 'pointer',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                  >
                    <LoopIcon style={{ fontSize: '14px' }} />
                    <span>×{maxIterations || 10}</span>
                  </div>
                </Tooltip>
              </EdgeLabelRenderer>
            )}


            {CAN_SHOW_TTC_ON_LINK.indexOf(mode) !== -1 && label && !(data && data.hasDb) && !isLoopEdge && (
              <foreignObject
                width={label.length * 10}
                height={60}
                x={edgeCenterX - (label.length * 9) / 2}
                y={edgeCenterY - foreignObjectSize / 2}
              >
                <textPath
                  href={`#${id}`}
                  style={{
                    fontSize: '12px',
                    backgroundColor: 'rgba(220, 237, 228, 1)',
                    border: '1px solid rgba(85, 167, 122, 1)',
                    borderRadius: '6px',
                    width: 'fit-content',
                    padding: '0px 4px',
                  }}
                  startOffset="50%"
                  textAnchor="middle"
                  id={`time-elapsed-${source}-${target}`}
                >
                  {label}
                </textPath>
              </foreignObject>
            )}
          </>
        );
      }
};

export default withStyles(styles)(Link);
